// src/pages/Manufacturing/ManufacturingRecord/components/exportManufacturingRecordCsv.js
import dayjs from "dayjs";

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const fmtNum = (val) => Number(val || 0).toFixed(2);
const fmtDate = (val) => (val ? dayjs(val).format("DD-MMM-YYYY") : "-");

/**
 * @param {object} params
 * @param {Array}  params.list    - from Redux state.manufacturingRecord.list
 * @param {object} params.filters - { filterType, recordType, fromDate, toDate }
 * @param {string} params.title
 * @param {string} params.filename
 */
export const exportManufacturingRecordCsv = ({
  list,
  filters,
  title    = "Manufacturing Record",
  filename = "manufacturing_record",
}) => {
  const isWithItem = filters.recordType === "with item";

  const rows = [
    [title],
    [`From: ${fmtDate(filters.fromDate)}  To: ${fmtDate(filters.toDate)}`],
    [`Record Type: ${isWithItem ? "With Item" : "Without Item"}`],
    [],
  ];

  if (isWithItem) {
    // ── With Item mode ───────────────────────────────────────
    const hasDiscount = list.some((r) => r.discount && r.discount !== "0%");
    const hasVat      = list.some((r) => r.vat      && r.vat      !== "0%");

    const headers = [
      "No.", "Voucher No", "Date", "Created By",
      "Item", "Rate",
      ...(hasDiscount ? ["Discount%"] : []),
      ...(hasVat      ? ["VAT%"]      : []),
      "Qty", "Total",
      "Master U. Qty", "Base U. Qty",
    ].map(escape);

    rows.push(headers);

    list.forEach((row, i) => {
      rows.push([
        i + 1,
        row.vch_no     || "",
        fmtDate(row.date),
        row.createdBy  || "",
        row.item       || "",
        fmtNum(row.rate),
        ...(hasDiscount ? [row.discount || ""] : []),
        ...(hasVat      ? [row.vat      || ""] : []),
        row.qty        || "",
        fmtNum(row.total),
        row.masterQty  ?? 0,
        row.baseQty    ?? 0,
      ].map(escape));
    });

    // Grand total — mirrors table summary
    const totals = list.reduce(
      (acc, row) => {
        acc.total      += Number(row.total     || 0);
        acc.master_qty += Number(row.masterQty || 0);
        acc.base_qty   += Number(row.baseQty   || 0);
        return acc;
      },
      { total: 0, master_qty: 0, base_qty: 0 }
    );

    const totalColCount = headers.length;
    const grandTotalRow = Array(totalColCount).fill("");
    grandTotalRow[0] = `Grand Total: Master U. Qty = ${totals.master_qty}, Base U. Qty = ${totals.base_qty}`;
    grandTotalRow[totalColCount - 3] = fmtNum(totals.total); // Total column

    rows.push([]);
    rows.push(grandTotalRow.map(escape));

  } else {
    // ── Without Item mode (summary) ──────────────────────────
    const headers = [
      "No.", "Voucher No", "Date", "Created By",
      "Labour Cost", "Others Cost", "Total Cost",
    ].map(escape);

    rows.push(headers);

    list.forEach((row, i) => {
      rows.push([
        i + 1,
        row.vch_no    || "",
        fmtDate(row.date),
        row.createdBy || "",
        fmtNum(row.labour),
        fmtNum(row.others),
        fmtNum(row.totalCost),
      ].map(escape));
    });

    // Grand total — mirrors table summary
    const totals = list.reduce(
      (acc, row) => {
        acc.labour    += Number(row.labour    || 0);
        acc.others    += Number(row.others    || 0);
        acc.totalCost += Number(row.totalCost || 0);
        return acc;
      },
      { labour: 0, others: 0, totalCost: 0 }
    );

    rows.push([]);
    rows.push([
      "Grand Total", "", "", "",
      fmtNum(totals.labour),
      fmtNum(totals.others),
      fmtNum(totals.totalCost),
    ].map(escape));
  }

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};